"use client";

import type { HeroSlide } from "@/lib/content/types";
import type { HeroOffer, HeroPanel } from "@/lib/pages/blocks";

import { motion } from "framer-motion";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { EASE } from "@/lib/motion";
import { cn } from "@/lib/cn";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";
import { useContent } from "@/components/shell/ContentContext";
import { useShell } from "@/components/shell/ShellContext";
import { useIdentity } from "@/lib/api/session";
import { useI18n } from "@/lib/i18n/I18nProvider";

const INTERVAL = 6500;

/* `size` is the kit's frame: large the homepage's 600px hero, small the
   400px one the casino lobby opens with. "scene" is a tall illustration with
   the copy at the top, fading into the page at the bottom, and the blocks
   after it rise over its lower part (Nebula). `copy` off draws each slide as
   a banner — the image alone, no title or button — and the whole banner
   links where the slide's button would have. "offer" is its own layout,
   OfferHero below, and so is "peek", PeekHero. */
export function Hero({
  offer,
  ...props
}: {
  slides: HeroSlide[];
  size?: "large" | "small" | "scene" | "headline" | "offer" | "peek";
  copy?: boolean;
  /* The sign-up panel beside the slides, when the block has one. */
  panel?: HeroPanel | null;
  /* The Offer hero's furniture, when the block is one. */
  offer?: HeroOffer | null;
}) {
  if (props.size === "offer" && offer) return <OfferHero slides={props.slides} offer={offer} />;
  if (props.size === "peek") return <PeekHero slides={props.slides} copy={props.copy} />;

  return <CarouselHero {...props} size={props.size === "offer" ? "large" : props.size} />;
}

function CarouselHero({
  slides,
  size = "large",
  copy = true,
  panel = null,
}: {
  slides: HeroSlide[];
  size?: "large" | "small" | "scene" | "headline";
  copy?: boolean;
  /* The sign-up panel beside the slides, when the block has one. */
  panel?: HeroPanel | null;
}) {
  const { isSignedIn } = useIdentity();
  const beside = panel && !isSignedIn ? panel : null;
  const scene = size === "scene";
  /* "headline": no frame — the copy over the page itself, left, in large
     display type with the brand gradient, and the slide's image as art on
     the right (Rigel). */
  const headline = size === "headline";
  const dots = Math.max(slides.length, 1);
  const [index, setIndex] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const go = useCallback(
    (next: number) => {
      setIndex(((next % dots) + dots) % dots);
    },
    [dots],
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);

    sync();
    query.addEventListener("change", sync);

    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (stopped || interacting || reducedMotion) return;

    const t = setInterval(() => go(index + 1), INTERVAL);
    return () => clearInterval(t);
  }, [index, go, stopped, interacting, reducedMotion]);

  /* Touch is the slide gesture on a phone — the arrows are pointer furniture
     and are hidden there. A ref rather than state: the start position is not
     something to render. */
  const touchStartX = useRef<number | null>(null);

  if (slides.length === 0) return beside ? <SignUpPanel panel={beside} /> : null;

  const slide = slides[index % slides.length];

  /* The wrapper is the panel's frame: over the slides' right side on
     desktop, under them on a phone. It is its own stacking context, so the
     blocks after a scene — pulled up over its lower part — paint above it. */
  return (
    <div className={cn("relative isolate", scene && "-mb-[140px] lg:-mb-[260px]")}>
    <section
      data-hero=""
      aria-roledescription="carousel"
      aria-label="Featured"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocus={() => setInteracting(true)}
      onBlur={() => setInteracting(false)}
      onTouchStart={(event) => {
        setInteracting(true);
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        setInteracting(false);

        const start = touchStartX.current;
        touchStartX.current = null;

        if (start === null || slides.length < 2) return;

        const delta = (event.changedTouches[0]?.clientX ?? start) - start;

        /* Under 48px is a tap or a scroll wobble, not a swipe. */
        if (Math.abs(delta) > 48) {
          go(index + (delta < 0 ? 1 : -1));
        }
      }}
      className={cn(
        "group relative overflow-hidden",
        !headline && "hero-wash",
        /* Headline sits framed in the page's own column (PageRenderer's
           CONTAINER: 1144 wide, 16/24px sides), with the same gap under the
           top bar as the sections below it, rounded like every card. */
        headline &&
          "mx-[16px] mt-[12px] rounded-2xl border border-border bg-elevation-1/60 lg:mx-auto lg:mt-[24px] lg:w-[calc(100%-48px)] lg:max-w-[1096px]",
      )}
    >
      {headline ? (
        slide.imageUrl && (
          <motion.img
            key={`art-${index}`}
            src={slide.imageUrl}
            alt=""
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="pointer-events-none absolute right-[32px] top-1/2 hidden h-[86%] max-w-[44%] -translate-y-1/2 object-contain lg:block"
          />
        )
      ) : slide.imageUrl ? (
        <>
          <motion.img
            key={`art-${index}`}
            src={slide.imageUrl}
            alt=""
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
            className={cn("absolute inset-0 h-full w-full object-cover", scene && "object-top")}
          />
          {scene ? (
            <span className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent via-45% to-canvas" />
          ) : (
            <span className="absolute inset-0 bg-gradient-to-t from-scrim-strong via-scrim to-transparent" />
          )}
        </>
      ) : (
        /* placeholder art marker, exactly as the kit shows it */
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-fg-disabled">
          <Icon name="image" size={35} />
        </span>
      )}

      {/* In flow rather than absolutely anchored, so on a phone the banner
          grows to hold a wrapped title instead of clipping it against the
          overflow-hidden edge. The frame's fixed kit proportion and cap —
          1920:600 and 600px large, 1920:400 and 400px small — apply from lg
          up, where the copy always fits; below that min-h keeps short slides
          from collapsing to a strip. The bottom padding reserves the dots'
          and pause control's zone. */}
      <div
        className={headline ? "relative flex min-h-[240px] w-full flex-col justify-center px-[20px] pb-[40px] pt-[40px] lg:min-h-[400px] lg:px-[40px] lg:pr-[44%]" : cn(
          "relative flex w-full flex-col items-center justify-end gap-[20px] px-[16px] pb-[64px] pt-[48px] md:px-[96px] lg:pb-[56px]",
          /* With the panel on the right, the copy moves to the left half. */
          beside && "lg:items-start lg:pr-[460px]",
          scene
            ? "min-h-[440px] justify-start pt-[56px] lg:min-h-[700px] lg:pt-[96px]"
            : size === "small"
              ? "min-h-[200px] lg:aspect-[1920/400] lg:max-h-[400px]"
              : "min-h-[280px] lg:aspect-[1920/600] lg:max-h-[600px]",
        )}
      >
        {!copy && slide.href && (
          <Link href={slide.href} aria-label={slide.title || slide.cta || "Open"} className="absolute inset-0 z-[5]" />
        )}
        {copy && (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            data-on-image={!headline && !scene ? "" : undefined}
            className={cn("flex flex-col items-center gap-[20px]", (beside || headline) && "lg:items-start", headline && "items-start")}
          >
            {(slide.title || slide.body) && (
              // The kit drops the hero a step on mobile: H5 over H6, H3 over H5 wide.
              <div
                className={cn(
                  "flex flex-col items-center gap-[12px] text-center",
                  beside && "lg:items-start lg:text-left",
                  headline && "items-start text-left",
                )}
              >
                {slide.title && (
                  <h1
                    className={cn(
                      headline
                        ? "bg-gradient-to-l from-brand-fg-accent from-35% to-fg to-70% bg-clip-text font-heading text-[32px] font-black leading-[1.05] text-transparent sm:text-[44px] lg:text-[52px]"
                        : "text-h5 font-black text-fg lg:text-h3",
                      scene && "max-w-[520px] text-balance",
                    )}
                  >
                    {slide.title}
                  </h1>
                )}
                {slide.body && <p className="text-h6 font-normal text-fg-subtle lg:text-h5">{slide.body}</p>}
              </div>
            )}
            {slide.cta && (
              <Link href={slide.href}>
                <Button size="lg" data-glow="">{slide.cta}</Button>
              </Link>
            )}
          </motion.div>
        )}
      </div>

      {slides.length > 1 && (
        <>
          <HeroArrow side="left" onClick={() => go(index - 1)} />
          <HeroArrow side="right" onClick={() => go(index + 1)} />
        </>
      )}

      <button
        type="button"
        onClick={() => setStopped((value) => !value)}
        aria-pressed={stopped}
        aria-label={stopped ? "Play slideshow" : "Pause slideshow"}
        className={cn(
          "absolute right-[25px] z-10 grid h-[24px] w-[24px] place-items-center rounded-xl bg-scrim text-fg transition-colors duration-300 hover:bg-scrim-strong",
          scene ? "top-[19px]" : "bottom-[19px]",
          scene && slides.length < 2 && "hidden",
        )}
      >
        <Icon name={stopped ? "play" : "pause"} size={14} />
      </button>

      {slides.length > 1 && (
        <div className={cn("absolute inset-x-0 flex h-[12px] items-center justify-center gap-[4px]", scene ? "top-[25px]" : "bottom-[25px]")}>
          {Array.from({ length: dots }).map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => go(i)}
              className={cn(
                "h-[12px] rounded-rounded transition-all duration-300",
                i === index ? "w-[38px] bg-brand" : "w-[23px] bg-elevation-4 hover:bg-elevation-3",
              )}
            />
          ))}
        </div>
      )}
    </section>
    {/* A scene's lower part lies under the blocks pulled up over it, so
        there the panel stands at the top beside the copy rather than at the
        middle, where the next block covered its lower half (StarBet). */}
    {beside && (
      <div
        className={cn(
          "px-[16px] pt-[16px] lg:pointer-events-none lg:absolute lg:right-[48px] lg:flex lg:p-0",
          scene ? "lg:top-[72px]" : "lg:inset-y-0 lg:items-center",
        )}
      >
        <SignUpPanel panel={beside} />
      </div>
    )}
    </div>
  );
}

function HeroArrow({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={side === "left" ? "Previous slide" : "Next slide"}
      className={cn(
        /* Hidden on touch widths: at 375px the two circles cover ~71px each
           side of a headline that only has 16px of padding. Swiping is the
           gesture there; these are for a pointer. */
        "absolute top-1/2 -translate-y-1/2 z-10 hidden h-[46px] w-[46px] place-items-center rounded-full bg-scrim text-fg transition-colors duration-300 hover:bg-scrim-strong md:grid",
        side === "left" ? "left-[25px]" : "right-[25px]",
      )}
    >
      <Icon name={side === "left" ? "arrow-left" : "arrow-right"} size={20} />
    </button>
  );
}

/* The hero's sign-up panel: the operator's pitch, an e-mail field, and a
   button that opens the sign-in with that address filled in — the platform's
   sign-in creates the account on first use, so this is registration. */
function SignUpPanel({ panel }: { panel: HeroPanel }) {
  const { openSignIn } = useShell();
  const { t } = useI18n();
  const [email, setEmail] = useState("");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        openSignIn(email.trim());
      }}
      className="modal-shadow pointer-events-auto flex w-full flex-col gap-[14px] rounded-2xl border border-border bg-elevation-1/95 p-[20px] backdrop-blur-[6px] lg:w-[380px] lg:p-[24px]"
    >
      {panel.title && <h2 className="text-[20px] font-black leading-[1.2] text-fg lg:text-[22px]">{panel.title}</h2>}
      {panel.body && <p className="text-[13px] leading-[1.6] text-fg-subtle">{panel.body}</p>}
      {panel.points.length > 0 && (
        <ul className="flex flex-col gap-[8px]">
          {panel.points.map((point) => (
            <li key={point} className="flex items-start gap-[8px] text-[13px] font-bold leading-[1.5] text-fg">
              <Icon name="check-circle" size={16} className="mt-[2px] shrink-0 text-brand-bold" />
              {point}
            </li>
          ))}
        </ul>
      )}
      <label className="flex h-[44px] items-center gap-[8px] rounded-lg border border-border bg-elevation-2 px-[14px] transition-colors focus-within:border-brand">
        <Icon name="mail" size={16} className="shrink-0 text-fg-subtle" />
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t("common.email")}
          aria-label={t("common.email")}
          autoComplete="email"
          className="h-full min-w-0 flex-1 bg-transparent text-[13px] font-bold text-fg outline-none placeholder:text-fg-subtle"
        />
      </label>
      <Button type="submit" size="lg" className="w-full">
        {panel.cta || t("topbar.register")}
      </Button>
      <button
        type="button"
        onClick={() => openSignIn()}
        className="text-center text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:text-fg"
      >
        {t("topbar.signIn")}
      </button>
    </form>
  );
}

/* The tilted art cards behind the Offer hero's image, as Betmode stacks
   them: the first high, the second low and in front (from a small tablet
   up), the third half out of the frame and dimmed. */
const ART_CARDS = [
  "right-[-30px] top-[18px] z-[1] w-[160px] rotate-[9deg] lg:right-[38px] lg:w-[180px]",
  "bottom-[2px] right-[-8px] z-[2] hidden w-[160px] rotate-[8deg] sm:block lg:w-[180px]",
  "right-[-55px] top-[92px] z-[1] w-[132px] rotate-[14deg] opacity-[0.68] lg:right-[-72px] lg:w-[148px]",
];

/* "offer" (Capella's, after Betmode): no frame. The copy on the left in
   huge display type — an eyebrow, the title with an outlined line under it,
   the text, the button with a second link beside it and a row of ticked
   points — and on the right the slide's image as art over tilted cards,
   floating chips and an offer card whose button opens the sign-in. With
   more than one slide the copy and the art rotate; the furniture stays. */
function OfferHero({ slides, offer }: { slides: HeroSlide[]; offer: HeroOffer }) {
  const { isSignedIn } = useIdentity();
  const { openSignIn } = useShell();
  const [index, setIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);

    sync();
    query.addEventListener("change", sync);

    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (slides.length < 2 || reducedMotion) return;

    const t = setInterval(() => setIndex((current) => (current + 1) % slides.length), INTERVAL);
    return () => clearInterval(t);
  }, [slides.length, reducedMotion]);

  const slide = slides.length > 0 ? slides[index % slides.length] : null;
  const card = offer.card;
  const cardButton = card && (
    <Button size="lg" iconRight="arrow-right" className="w-full" onClick={isSignedIn ? undefined : () => openSignIn()}>
      {card.cta || "Claim offer"}
    </Button>
  );

  return (
    <section
      data-hero=""
      aria-label="Featured"
      className="relative isolate mx-auto grid w-full max-w-[1368px] items-center gap-[24px] overflow-x-clip px-[16px] pb-[48px] pt-[48px] lg:min-h-[660px] lg:grid-cols-[minmax(0,0.94fr)_minmax(420px,1.06fr)] lg:gap-[56px] lg:px-[24px] lg:pb-[76px] lg:pt-[62px]"
    >
      <span aria-hidden className="pointer-events-none absolute right-[14%] top-[12%] -z-10 h-[250px] w-[250px] rounded-full bg-highlight/10 blur-[80px]" />
      <span aria-hidden className="pointer-events-none absolute bottom-[10%] left-[30%] -z-10 h-[200px] w-[200px] rounded-full bg-brand/5 blur-[80px]" />

      <motion.div
        key={`copy-${index}`}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative z-[3] mx-auto flex max-w-[650px] flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left"
      >
        {offer.eyebrow && (
          <div className="flex items-center gap-[10px] text-[11px] font-black uppercase tracking-[0.19em] text-highlight">
            <span className="h-[7px] w-[7px] rounded-full bg-highlight shadow-[0_0_16px_var(--color-highlight)]" />
            {offer.eyebrow}
          </div>
        )}
        {slide?.title && (
          <h1
            style={{ letterSpacing: "-0.025em" }}
            className="mt-[22px] font-heading text-[clamp(52px,15vw,73px)] font-black uppercase leading-[0.9] text-fg lg:text-[clamp(64px,6.2vw,94px)]"
          >
            {slide.title}
            {slide.outline && (
              <span className="block text-transparent [-webkit-text-stroke:1px_color-mix(in_srgb,var(--color-fg)_65%,transparent)]">
                {slide.outline}
              </span>
            )}
          </h1>
        )}
        {slide?.body && <p className="mt-[28px] max-w-[565px] text-[14px] leading-[1.75] text-fg-subtle lg:text-[16px]">{slide.body}</p>}
        {(slide?.cta || offer.link) && (
          <div className="mt-[32px] flex flex-col items-center gap-[17px] sm:flex-row sm:gap-[25px]">
            {slide?.cta && (
              <Link href={slide.href || "/casino"}>
                <Button size="lg" iconRight="arrow-right" data-glow="" className="h-[56px] px-[28px]">
                  {slide.cta}
                </Button>
              </Link>
            )}
            {offer.link && (
              <Link
                href={offer.link.href}
                className="inline-flex items-center gap-[5px] font-heading text-[13px] font-extrabold uppercase tracking-[0.045em] text-fg transition-colors hover:text-brand"
              >
                {offer.link.label}
                <Icon name="chevron-right" size={16} />
              </Link>
            )}
          </div>
        )}
        {offer.points.length > 0 && (
          <ul className="mt-[28px] flex flex-wrap justify-center gap-x-[18px] gap-y-[8px] lg:justify-start">
            {offer.points.map((point) => (
              <li key={point} className="inline-flex items-center gap-[6px] text-[11px] font-bold text-fg-subtle">
                <Icon name="check" size={16} className="text-highlight" />
                {point}
              </li>
            ))}
          </ul>
        )}
        {slides.length > 1 && (
          <div className="mt-[24px] flex items-center gap-[4px]">
            {slides.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={cn("h-[6px] rounded-rounded transition-all duration-300", i === index ? "w-[28px] bg-brand" : "w-[12px] bg-elevation-4")}
              />
            ))}
          </div>
        )}
      </motion.div>

      <div className="relative mx-auto h-[540px] w-full max-w-[610px] lg:h-[525px] lg:max-w-none">
        <span
          aria-hidden
          className="absolute inset-[35px_-80px_30px_20px] -rotate-[8deg] rounded-[50%] border border-highlight/10 bg-[radial-gradient(circle,color-mix(in_srgb,var(--color-highlight)_10%,transparent),transparent_63%)]"
        />
        {offer.artCards.map((url, i) => (
          <motion.img
            key={`${url}-${i}`}
            src={url}
            alt=""
            className={cn(
              "absolute aspect-[0.79] rounded-[13px] border border-white/15 object-cover shadow-[0_22px_60px_rgba(0,0,0,0.45)]",
              ART_CARDS[i],
            )}
          />
        ))}
        {slide?.imageUrl && (
          <motion.img
            key={`art-${index}`}
            src={slide.imageUrl}
            alt=""
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="pointer-events-none absolute bottom-[145px] right-0 z-[4] w-[335px] max-w-none [filter:drop-shadow(-14px_8px_28px_rgba(0,0,0,0.52))_drop-shadow(0_0_18px_color-mix(in_srgb,var(--color-highlight)_10%,transparent))] [mask-image:linear-gradient(to_bottom,#000_0%,#000_84%,transparent_100%)] lg:bottom-[24px] lg:right-[46px] lg:w-[410px]"
          />
        )}
        {offer.chips.map((chip, i) => (
          <div
            key={chip}
            className={cn(
              "absolute z-[8] flex items-center gap-[7px] rounded-[30px] border border-white/10 bg-elevation-1/90 px-[13px] py-[10px] text-[10px] font-extrabold text-fg-muted shadow-[0_12px_30px_rgba(0,0,0,0.32)] backdrop-blur-[12px]",
              i === 0 ? "left-[-4px] top-[72px] lg:left-[25px] lg:top-[96px]" : "bottom-[6px] right-[10px] hidden sm:flex",
            )}
          >
            <Icon name={i === 0 ? "bolt" : "shield-check"} size={16} className="text-highlight" />
            {chip}
          </div>
        ))}
        {card && (
          <div
            style={{
              background:
                "linear-gradient(140deg, color-mix(in srgb, var(--color-brand) 8%, transparent), transparent 44%), color-mix(in srgb, var(--color-elevation-1) 94%, transparent)",
            }}
            className="absolute bottom-[10px] left-0 z-[6] w-[min(calc(100%-35px),326px)] rounded-[14px] border border-brand/30 p-[21px] shadow-[0_28px_80px_rgba(0,0,0,0.5)] backdrop-blur-[16px] lg:bottom-[30px] lg:left-[5px] lg:w-[326px]"
          >
            <div className="flex justify-between text-[10px] font-black uppercase tracking-[0.15em] text-fg-subtle">
              <span>{card.label}</span>
              {card.tag && (
                <span className="flex items-center gap-[6px] text-brand">
                  <i className="h-[5px] w-[5px] rounded-full bg-brand shadow-[0_0_8px_var(--color-brand)]" />
                  {card.tag}
                </span>
              )}
            </div>
            <div className="my-[20px] flex flex-col text-left">
              {card.kicker && <span className="text-[13px] font-bold text-fg-muted">{card.kicker}</span>}
              {card.amount && <strong className="font-heading text-[66px] font-black leading-none text-fg">{card.amount}</strong>}
              {card.extra && <em className="text-[14px] font-black not-italic text-brand">{card.extra}</em>}
            </div>
            {isSignedIn ? <Link href="/promotions">{cardButton}</Link> : cardButton}
            {card.note && <small className="mt-[12px] block text-center text-[9px] text-fg-subtle">{card.note}</small>}
          </div>
        )}
      </div>
    </section>
  );
}

/* "peek" (Aldebaran's, after Topzinho): wide banners in a row, the current
   one centred and its neighbours showing, dimmed, at both sides, the row
   looping round. Such banners carry their own words, so the copy is
   optional: without it the whole banner links where its button would. The
   row is the slides with the last one before them and the first one after,
   so both neighbours always exist; after sliding onto one of those copies it
   jumps, unanimated, to the slide it copies. */
function PeekHero({ slides, copy = false }: { slides: HeroSlide[]; copy?: boolean }) {
  const count = slides.length;
  const loop = count > 1;
  const row = loop ? [slides[count - 1], ...slides, slides[0]] : slides;
  const [position, setPosition] = useState(loop ? 1 : 0);
  const [animate, setAnimate] = useState(true);
  const [interacting, setInteracting] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);

    sync();
    query.addEventListener("change", sync);

    return () => query.removeEventListener("change", sync);
  }, []);

  /* Without motion no transition ends, so the jump off a copy happens here. */
  const wrap = useCallback((next: number) => (next < 1 ? count : next > count ? 1 : next), [count]);

  const step = useCallback(
    (delta: number) => {
      if (!loop) return;
      setAnimate(!reducedMotion);
      setPosition((current) => (reducedMotion ? wrap(current + delta) : Math.min(Math.max(current + delta, 0), count + 1)));
    },
    [loop, reducedMotion, wrap, count],
  );

  useEffect(() => {
    if (!loop || interacting || reducedMotion) return;

    const t = setInterval(() => step(1), INTERVAL);
    return () => clearInterval(t);
  }, [loop, interacting, reducedMotion, step]);

  /* Back on: two frames after the unanimated jump has been painted. */
  useEffect(() => {
    if (animate || reducedMotion) return;

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setAnimate(true));
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [animate, reducedMotion]);

  if (count === 0) return null;

  return (
    <section
      data-hero=""
      aria-roledescription="carousel"
      aria-label="Featured"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocus={() => setInteracting(true)}
      onBlur={() => setInteracting(false)}
      onTouchStart={(event) => {
        setInteracting(true);
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        setInteracting(false);

        const start = touchStartX.current;
        touchStartX.current = null;

        if (start === null) return;

        const delta = (event.changedTouches[0]?.clientX ?? start) - start;

        if (Math.abs(delta) > 48) step(delta < 0 ? 1 : -1);
      }}
      className="relative mt-[12px] overflow-hidden [--peek-gap:8px] [--peek-w:calc(100%_-_40px)] md:[--peek-gap:12px] md:[--peek-w:calc(100%_-_128px)] lg:mt-[16px] lg:[--peek-w:min(1248px,calc(100%_-_176px))]"
    >
      <div
        onTransitionEnd={(event) => {
          if (event.target !== event.currentTarget || !loop) return;
          if (position === 0 || position === count + 1) {
            setAnimate(false);
            setPosition(wrap(position));
          }
        }}
        className={cn("flex w-full gap-[var(--peek-gap)]", animate && !reducedMotion && "transition-transform duration-500 ease-out")}
        style={{
          transform: loop
            ? `translateX(calc((100% - var(--peek-w)) / 2 - ${position} * (var(--peek-w) + var(--peek-gap))))`
            : "translateX(calc((100% - var(--peek-w)) / 2))",
        }}
      >
        {row.map((slide, i) => {
          const current = i === position;

          return (
            <div
              key={i}
              aria-hidden={!current}
              className={cn(
                "relative aspect-[16/7] w-[var(--peek-w)] flex-none overflow-hidden rounded-xl bg-elevation-1 transition-opacity duration-500 md:aspect-[1248/300]",
                current ? "opacity-100" : "opacity-40",
              )}
            >
              {slide.imageUrl ? (
                <img src={slide.imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-fg-disabled">
                  <Icon name="image" size={35} />
                </span>
              )}
              {!copy && slide.href && (
                <Link
                  href={slide.href}
                  tabIndex={current ? 0 : -1}
                  aria-label={slide.title || slide.cta || "Open"}
                  className="absolute inset-0 z-[5]"
                />
              )}
              {copy && current && (slide.title || slide.cta) && (
                <div className="absolute inset-0 flex flex-col items-start justify-center gap-[10px] bg-gradient-to-r from-scrim-strong via-scrim to-transparent px-[20px] md:px-[40px]">
                  {slide.title && <h2 className="max-w-[60%] text-h6 font-black text-fg lg:text-h4">{slide.title}</h2>}
                  {slide.body && <p className="hidden max-w-[50%] text-[14px] text-fg-subtle md:block">{slide.body}</p>}
                  {slide.cta && (
                    <Link href={slide.href} tabIndex={current ? 0 : -1}>
                      <Button size="md" data-glow="">{slide.cta}</Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loop && (
        <>
          <PeekArrow side="left" onClick={() => step(-1)} />
          <PeekArrow side="right" onClick={() => step(1)} />
        </>
      )}
    </section>
  );
}

/* On the dimmed neighbours, centred in the strip they show. */
function PeekArrow({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Previous slide" : "Next slide"}
      className={cn(
        "absolute top-1/2 z-10 hidden h-[36px] w-[36px] -translate-y-1/2 place-items-center rounded-lg bg-scrim text-fg transition-colors duration-300 hover:bg-scrim-strong md:grid",
        side === "left"
          ? "left-[calc((100%_-_var(--peek-w))_/_4_-_18px)]"
          : "right-[calc((100%_-_var(--peek-w))_/_4_-_18px)]",
      )}
    >
      <Icon name={side === "left" ? "arrow-left" : "arrow-right"} size={18} />
    </button>
  );
}
